import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

/**
 * Custom hook to synchronize URL query parameters with filters and pagination
 */
const useUrlSync = (filters, pagination, onFiltersChange, onPaginationChange) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Parse URL parameters and return filter object
    const parseUrlParams = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlFilters = {};
        
        // Parse basic filters
        const search = searchParams.get('search');
        if (search) urlFilters.searchText = search;
        
        const status = searchParams.get('status');
        if (status) {
            // Handle multiple status values separated by comma
            if (status.includes(',')) {
                urlFilters.selectedStatus = status.split(',').map(s => s.trim()).filter(s => s.length > 0);
            } else if (status.trim().length > 0) {
                urlFilters.selectedStatus = [status.trim()];
            } else {
                urlFilters.selectedStatus = [];
            }
        } else {
            urlFilters.selectedStatus = [];
        }
        
        const sellerAccount = searchParams.get('sellerAccount');
        if (sellerAccount) {
            // Handle comma-separated seller account values
            if (sellerAccount.includes(',')) {
                urlFilters.selectedSellerAccount = sellerAccount.split(',').map(s => s.trim()).filter(s => s.length > 0);
            } else {
                urlFilters.selectedSellerAccount = [sellerAccount];
            }
        }
        
        const customizationStatus = searchParams.get('customizationStatus');
        if (customizationStatus) urlFilters.selectedCustomizationStatus = customizationStatus;
        
        const printer = searchParams.get('printer');
        if (printer) {
            // Handle comma-separated printer values
            if (printer.includes(',')) {
                urlFilters.selectedPrinter = printer.split(',').map(p => p.trim()).filter(p => p.length > 0);
            } else {
                urlFilters.selectedPrinter = [printer];
            }
        }
        
        const fulfillmentStatus = searchParams.get('fulfillmentStatus');
        if (fulfillmentStatus) urlFilters.selectedFulfillmentStatus = fulfillmentStatus;
        
        const fulfillmentChannel = searchParams.get('fulfillmentChannel');
        if (fulfillmentChannel) urlFilters.selectedFulfillmentChannel = fulfillmentChannel;
        
        const shipmentStatus = searchParams.get('shipmentStatus');
        if (shipmentStatus) {
            // Handle multiple shipment status values separated by comma
            if (shipmentStatus.includes(',')) {
                urlFilters.selectedShipmentStatus = shipmentStatus.split(',').map(s => s.trim()).filter(s => s.length > 0);
            } else if (shipmentStatus.trim().length > 0) {
                urlFilters.selectedShipmentStatus = [shipmentStatus.trim()];
            } else {
                urlFilters.selectedShipmentStatus = [];
            }
        } else {
            urlFilters.selectedShipmentStatus = [];
        }
        
        const supporter = searchParams.get('supporter');
        if (supporter) urlFilters.selectedSupporter = supporter;
        
        const category = searchParams.get('category');
        if (category) {
            // Handle comma-separated category values
            if (category.includes(',')) {
                urlFilters.selectedCategory = category.split(',').map(c => c.trim()).filter(c => c.length > 0);
            } else {
                urlFilters.selectedCategory = [category];
            }
        }
        
        const messageStatus = searchParams.get('messageStatus');
        if (messageStatus) urlFilters.selectedMessageStatus = messageStatus;
        
        const shipDateDue = searchParams.get('shipDateDue');
        if (shipDateDue) urlFilters.selectedShipDateDue = shipDateDue;
        
        const deliveryDateDue = searchParams.get('deliveryDateDue');
        if (deliveryDateDue) urlFilters.selectedDeliveryDateDue = deliveryDateDue;
        
        const confirmShipment = searchParams.get('confirmShipment');
        if (confirmShipment) urlFilters.selectedConfirmShipment = confirmShipment;
        
        const updatedLastMile = searchParams.get('updatedLastMile');
        if (updatedLastMile) urlFilters.selectedUpdatedLastMile = updatedLastMile;
        
        const mockupSentAt = searchParams.get('mockupSentAt');
        if (mockupSentAt) urlFilters.selectedMockupSentAt = mockupSentAt;
        
        const title = searchParams.get('title');
        if (title) urlFilters.selectedTitle = title;
        
        const sellerSku = searchParams.get('sellerSku');
        if (sellerSku) urlFilters.selectedSellerSku = sellerSku;
        
        const employeeId = searchParams.get('employeeId');
        if (employeeId) urlFilters.selectedEmployeeId = employeeId;
        
        const excludeTitle = searchParams.get('excludeTitle');
        if (excludeTitle) urlFilters.selectedExcludeTitle = excludeTitle;
        
        const excludeSellerSku = searchParams.get('excludeSellerSku');
        if (excludeSellerSku) urlFilters.selectedExcludeSellerSku = excludeSellerSku;
        
        // Parse date range
        const startDate = searchParams.get('startDate');
        const endDate = searchParams.get('endDate');
        if (startDate && endDate) {
            try {
                const start = dayjs(startDate);
                const end = dayjs(endDate);
                if (start.isValid() && end.isValid()) {
                    urlFilters.selectedDateRange = [start, end];
                }
            } catch (error) {
                console.warn('Invalid date range in URL:', error);
            }
        }
        
        // Parse pagination
        const page = searchParams.get('page');
        const pageSize = searchParams.get('pageSize');
        const urlPagination = {};
        
        if (page) {
            const pageNum = parseInt(page, 10);
            if (!isNaN(pageNum) && pageNum > 0) {
                urlPagination.current = pageNum;
            }
        }
        
        if (pageSize) {
            const pageSizeNum = parseInt(pageSize, 10);
            if (!isNaN(pageSizeNum) && pageSizeNum > 0) {
                urlPagination.pageSize = pageSizeNum;
            }
        }
        
        return { urlFilters, urlPagination };
    }, [location.search]);

    // Update URL with current filters and pagination
    const updateUrl = useCallback((newFilters, newPagination) => {
        const searchParams = new URLSearchParams();
        
        // Add filter params
        if (newFilters.searchText) {
            searchParams.set('search', newFilters.searchText);
        }
        
        if (newFilters.selectedStatus && Array.isArray(newFilters.selectedStatus) && newFilters.selectedStatus.length > 0) {
            searchParams.set('status', newFilters.selectedStatus.join(','));
        }
        
        if (newFilters.selectedSellerAccount && Array.isArray(newFilters.selectedSellerAccount) && newFilters.selectedSellerAccount.length > 0) {
            searchParams.set('sellerAccount', newFilters.selectedSellerAccount.join(','));
        }
        
        if (newFilters.selectedCustomizationStatus && newFilters.selectedCustomizationStatus !== 'all') {
            searchParams.set('customizationStatus', newFilters.selectedCustomizationStatus);
        }
        
        if (newFilters.selectedPrinter && Array.isArray(newFilters.selectedPrinter) && newFilters.selectedPrinter.length > 0) {
            searchParams.set('printer', newFilters.selectedPrinter.join(','));
        }
        
        if (newFilters.selectedFulfillmentStatus && newFilters.selectedFulfillmentStatus !== 'all') {
            searchParams.set('fulfillmentStatus', newFilters.selectedFulfillmentStatus);
        }
        
        if (newFilters.selectedFulfillmentChannel && newFilters.selectedFulfillmentChannel !== 'all') {
            searchParams.set('fulfillmentChannel', newFilters.selectedFulfillmentChannel);
        }
        
        if (newFilters.selectedShipmentStatus && Array.isArray(newFilters.selectedShipmentStatus) && newFilters.selectedShipmentStatus.length > 0) {
            searchParams.set('shipmentStatus', newFilters.selectedShipmentStatus.join(','));
        }
        
        if (newFilters.selectedSupporter && newFilters.selectedSupporter !== 'all') {
            searchParams.set('supporter', newFilters.selectedSupporter);
        }
        
        if (newFilters.selectedCategory && Array.isArray(newFilters.selectedCategory) && newFilters.selectedCategory.length > 0) {
            searchParams.set('category', newFilters.selectedCategory.join(','));
        }
        
        if (newFilters.selectedMessageStatus && newFilters.selectedMessageStatus !== 'all') {
            searchParams.set('messageStatus', newFilters.selectedMessageStatus);
        }
        
        if (newFilters.selectedShipDateDue && newFilters.selectedShipDateDue !== 'all') {
            searchParams.set('shipDateDue', newFilters.selectedShipDateDue);
        } else {
            searchParams.delete('shipDateDue');
        }
        
        if (newFilters.selectedDeliveryDateDue && newFilters.selectedDeliveryDateDue !== 'all') {
            searchParams.set('deliveryDateDue', newFilters.selectedDeliveryDateDue);
        } else {
            searchParams.delete('deliveryDateDue');
        }
        
        if (newFilters.selectedConfirmShipment && newFilters.selectedConfirmShipment !== 'all') {
            searchParams.set('confirmShipment', newFilters.selectedConfirmShipment);
        } else {
            searchParams.delete('confirmShipment');
        }
        
        if (newFilters.selectedUpdatedLastMile && newFilters.selectedUpdatedLastMile !== 'all') {
            searchParams.set('updatedLastMile', newFilters.selectedUpdatedLastMile);
        } else {
            searchParams.delete('updatedLastMile');
        }
        
        if (newFilters.selectedMockupSentAt && newFilters.selectedMockupSentAt !== 'all') {
            searchParams.set('mockupSentAt', newFilters.selectedMockupSentAt);
        } else {
            searchParams.delete('mockupSentAt');
        }
        
        if (newFilters.selectedTitle && newFilters.selectedTitle.trim()) {
            searchParams.set('title', newFilters.selectedTitle.trim());
        } else {
            searchParams.delete('title');
        }
        
        if (newFilters.selectedSellerSku && newFilters.selectedSellerSku.trim()) {
            searchParams.set('sellerSku', newFilters.selectedSellerSku.trim());
        } else {
            searchParams.delete('sellerSku');
        }
        
        if (newFilters.selectedEmployeeId && newFilters.selectedEmployeeId.trim()) {
            searchParams.set('employeeId', newFilters.selectedEmployeeId.trim());
        } else {
            searchParams.delete('employeeId');
        }
        
        if (newFilters.selectedExcludeTitle && newFilters.selectedExcludeTitle.trim()) {
            searchParams.set('excludeTitle', newFilters.selectedExcludeTitle.trim());
        } else {
            searchParams.delete('excludeTitle');
        }
        
        if (newFilters.selectedExcludeSellerSku && newFilters.selectedExcludeSellerSku.trim()) {
            searchParams.set('excludeSellerSku', newFilters.selectedExcludeSellerSku.trim());
        } else {
            searchParams.delete('excludeSellerSku');
        }
        
        // Add date range params
        if (newFilters.selectedDateRange && newFilters.selectedDateRange.length === 2) {
            const startDate = dayjs(newFilters.selectedDateRange[0]).format('YYYY-MM-DD');
            const endDate = dayjs(newFilters.selectedDateRange[1]).format('YYYY-MM-DD');
            searchParams.set('startDate', startDate);
            searchParams.set('endDate', endDate);
        }
        
        // Add pagination params
        if (newPagination.current && newPagination.current > 1) {
            searchParams.set('page', newPagination.current.toString());
        }
        
        if (newPagination.pageSize && newPagination.pageSize !== 30) { // 30 is default
            searchParams.set('pageSize', newPagination.pageSize.toString());
        }
        
        // Update URL without triggering a page reload
        const newUrl = searchParams.toString() ? `${location.pathname}?${searchParams.toString()}` : location.pathname;
        navigate(newUrl, { replace: true });
    }, [navigate, location.pathname]);

    // Initialize filters from URL on component mount
    useEffect(() => {
        const { urlFilters, urlPagination } = parseUrlParams();
        
        // Only update if there are URL parameters
        if (Object.keys(urlFilters).length > 0 || Object.keys(urlPagination).length > 0) {
            // Merge URL filters with default filters
            const mergedFilters = {
                searchText: '',
                selectedStatus: [],
                selectedDateRange: null,
                selectedSellerAccount: [],
                selectedCustomizationStatus: 'all',
                selectedPrinter: [],
                selectedFulfillmentStatus: 'all',
                selectedFulfillmentChannel: 'all',
                selectedShipmentStatus: [],
                selectedSupporter: 'all',
                selectedCategory: [],
                selectedMessageStatus: 'all',
                selectedShipDateDue: 'all',
                selectedDeliveryDateDue: 'all',
                selectedConfirmShipment: 'all',
                selectedUpdatedLastMile: 'all',
                selectedMockupSentAt: 'all',
                selectedTitle: '',
                selectedSellerSku: '',
                selectedEmployeeId: '',
                selectedExcludeTitle: '',
                selectedExcludeSellerSku: '',
                ...urlFilters
            };
            
            // Merge URL pagination with default pagination
            const mergedPagination = {
                current: 1,
                pageSize: 30,
                total: 0, // This will be updated after API call
                ...urlPagination
            };
            
            // Update filters and pagination
            onFiltersChange(mergedFilters);
            onPaginationChange(mergedPagination);
        }
    }, [location.search]); // React to search params changes

    // Return the updateUrl function for components to use
    return { updateUrl };
};

export default useUrlSync;